import { useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box } from '@mui/material';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import 'bpmn-js/dist/assets/diagram-js.css';
import { DiagramProvider, useDiagramContext } from './Context/DiagramContext';
import DiagramHeader from './Components/DiagramHeader';
import DiagramCanvas from './Components/DiagramCanvas';
import DiagramModals from './Components/DiagramModal';
import DiagramSlides from './Components/DiagramSlides';
import DiagramNotifications from './Components/DiagramNotification';

import { useDiagramLifecycle } from './hooks/useDiagramLifecycle';
import { useDiagramLayout } from './hooks/useDiagramLayout';
import { useDiagramSave } from './hooks/useDiagramSave';
import { useDiagramZoom } from './hooks/useDiagramZoom';
import { useFetchAISolutions } from './hooks/useAiSolution';
import type { DiagramBpmnHandle, DiagramBpmnProps } from './Types/diagram-type';

const NAVBAR_HEIGHT_PX = 60;

type DiagramBpmnContentProps = {};

const DiagramBpmnContent = forwardRef<DiagramBpmnHandle, DiagramBpmnContentProps>((_props, ref) => {
  const { isExpanded, isMobile, isProcessXPLRActive } = useDiagramLayout();
  const {
    isProcessDiagram,
    isEditPermission,
    selectedCard,
    isDiagramChanged,
    setIsDiagramChanged,
    registerHandlers,
    setSolutionData,
    setDataSourceType,
    onClose,
    onDiagramChange,
    setShowUnsavedChangesModal,
    diagramData,
    activeModal,
  } = useDiagramContext();

  const { handleSaveDiagram, handleDiscardChanges } = useDiagramSave();
  const { handleZoomIn, handleZoomOut, handleFitViewToggle } = useDiagramZoom();

  const handleCloseHandler = useCallback(() => {
    if (isDiagramChanged) {
      setShowUnsavedChangesModal(true);
    } else if (onClose) {
      onClose();
    } else {
      window.history.back();
    }
  }, [onClose, isDiagramChanged, setShowUnsavedChangesModal]);

  const handleStepDescriptionUpdate = useCallback(() => {
    if (isEditPermission) {
      setIsDiagramChanged(true);
      if (onDiagramChange) onDiagramChange(true);
    }
  }, [
    isEditPermission,
    setIsDiagramChanged,
    onDiagramChange,
  ]);

  const handlersRef = useRef({
    handleSaveDiagram,
    handleDiscardChanges,
    handleZoomIn,
    handleZoomOut,
    handleFitViewToggle,
    handleClose: handleCloseHandler,
    handleStepDescriptionUpdate,
  });

  useEffect(() => {
    handlersRef.current = {
      handleSaveDiagram,
      handleDiscardChanges,
      handleZoomIn,
      handleZoomOut,
      handleFitViewToggle,
      handleClose: handleCloseHandler,
      handleStepDescriptionUpdate,
    };
  }, [
    handleSaveDiagram,
    handleDiscardChanges,
    handleZoomIn,
    handleZoomOut,
    handleFitViewToggle,
    handleCloseHandler,
    handleStepDescriptionUpdate,
  ]);

  useEffect(() => {
    registerHandlers({
      handleSaveDiagram: () => handlersRef.current.handleSaveDiagram(),
      handleDiscardChanges: () => handlersRef.current.handleDiscardChanges(),
      handleZoomIn: () => handlersRef.current.handleZoomIn(),
      handleZoomOut: () => handlersRef.current.handleZoomOut(),
      handleFitViewToggle: () => handlersRef.current.handleFitViewToggle(),
      handleClose: () => handlersRef.current.handleClose(),
      handleStepDescriptionUpdate: () => handlersRef.current.handleStepDescriptionUpdate(),
    });
  }, [registerHandlers]);

  useImperativeHandle(
    ref,
    () => ({
      saveDiagram: () => handlersRef.current.handleSaveDiagram(),
      discardChanges: () => handlersRef.current.handleDiscardChanges(),
    }),
    []
  );

  const { solutionData, dataSourceType } = useFetchAISolutions({
    diagramId: diagramData?.diagram?.id,
    selectedCardId: selectedCard?.id,
    activeModal,
  });

  useEffect(() => {
    setSolutionData(solutionData);
    // setDataSourceType(dataSourceType);
  }, [solutionData, dataSourceType, setSolutionData, setDataSourceType]);

  useDiagramLifecycle();

  return (
    <>
      <Box
        sx={{
          height: isProcessDiagram ? '100%' : '100vh',
          width: isProcessDiagram ? '100%' : isEditPermission ? 'auto' : '100%',
          position: isProcessDiagram ? 'relative' : 'fixed',
          top: isProcessDiagram ? 'auto' : isEditPermission ? 50 : 0,
          left: isProcessDiagram
            ? 'auto'
            : selectedCard?.processType === 'custom'
              ? isEditPermission
                ? 280
                : 0
              : 0,
          right: isProcessDiagram ? 'auto' : 0,
          bottom: isProcessDiagram ? 'auto' : 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#F4F6FF',
          ...(isExpanded && {
            ...(isProcessXPLRActive
              ? {
                  position: 'fixed !important',
                  top: isMobile ? '0 !important' : `${NAVBAR_HEIGHT_PX}px !important`,
                  left: '0 !important',
                  right: '0 !important',
                  bottom: '0 !important',
                  width: '100vw !important',
                  height: isMobile
                    ? '100vh !important'
                    : `calc(100vh - ${NAVBAR_HEIGHT_PX}px) !important`,
                  paddingTop: isMobile ? '68px' : 0, // Buffer for mobile navbar
                  zIndex: 1200,
                }
              : {
                  position: 'fixed !important',
                  top: '8px !important',
                  left: '0 !important',
                  right: '0 !important',
                  bottom: '0 !important',
                  width: '100vw !important',
                  height: 'calc(100vh - 8px) !important',
                  zIndex: 1200,
                }),
          }),
          ...(!isProcessDiagram &&
            isMobile && {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              backgroundColor: '#F4F6FF',
              overflow: 'hidden',
            }),
        }}
      >
        <Box
          sx={{
            position: 'relative',
            flexDirection: 'column',
            display: 'flex',
            mt: isExpanded ? 0 : isEditPermission ? 0 : 5,
            height: '100%',
            width: '100%',
            maxWidth: '100%',
            borderRadius: { xs: 0, md: 2 },
            backgroundColor: 'background.paper',
            boxShadow: { xs: 'none', md: 1 },
          }}
        >
          <DiagramHeader />
          <DiagramCanvas />
          <DiagramModals />
          <DiagramSlides />
          <DiagramNotifications />
        </Box>
      </Box>
    </>
  );
});

const DiagramBpmn = forwardRef<DiagramBpmnHandle, DiagramBpmnProps>((props, ref) => {
  return (
    <DiagramProvider {...props}>
      <DiagramBpmnContent ref={ref} />
    </DiagramProvider>
  );
});

export default DiagramBpmn;

export type { DiagramBpmnHandle } from './Types/diagram-type';
export type { Opportunity } from './Types/diagram-type';
